/**
 * "Truck billing" on the job (0600, fieldiq-api PUT /earthworks/jobs/:id/billing).
 *
 * Office only (admin, office_staff) — it decides what the customer is charged
 * for trucks: by the HOUR (a truck's logged hours bill, the loads it hauled
 * that day do not) or PER LOAD (the loads bill, the truck's hours that day do
 * not). The source says where the current value came from: set on this job,
 * read from the job's quote, or neither (hourly by default). A change applies
 * to tickets drafted or rebuilt from now on, never to one already built.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { useSetEwTruckBilling } from '../../hooks/useEarthworks';
import { ewRefusal } from '../../lib/earthworks';
import type { EwJobDetail, EwTruckBillingBasis, EwTruckBillingSource } from '../../types/earthworks';
import { EW_INPUT, EwFormError, EwPanel } from './EwBits';

const SOURCE: Record<EwTruckBillingSource, string> = {
  job: 'Set on this job.',
  quote: 'From the job’s quote.',
  default: 'Default — no quote sets it.',
};

export function TruckBillingPanel({ job }: { job: EwJobDetail }) {
  const set = useSetEwTruckBilling();
  const [error, setError] = useState<string | null>(null);
  if (!job.truckBillingBasis) return null;
  const source = job.truckBillingBasisSource ?? 'default';

  const change = (basis: EwTruckBillingBasis) => {
    setError(null);
    set.mutate({ id: job.id, basis }, {
      onSuccess: () => toast.success(`Trucks now bill ${basis === 'per_load' ? 'per load' : 'by the hour'} on new tickets`),
      onError: (err) => setError(ewRefusal(err, 'The truck billing could not be saved.')),
    });
  };

  return (
    <EwPanel eyebrow="Billing" title="Truck billing" testId="ew-truck-billing">
      <div className="space-y-2 px-4 py-4">
        <EwFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-[16rem_1fr] sm:items-center">
          <select
            className={EW_INPUT}
            aria-label="Truck billing"
            value={job.truckBillingBasis}
            disabled={set.isPending}
            onChange={(e) => change(e.target.value as EwTruckBillingBasis)}
          >
            <option value="hourly">Hourly</option>
            <option value="per_load">Per load</option>
          </select>
          <p className="text-sm text-fieldiq-muted" data-testid="ew-truck-billing-source">{SOURCE[source]}</p>
        </div>
        <p className="text-xs text-fieldiq-muted">
          A truck bills one way per day. Loads that name no owned truck always bill per load. Applies to tickets drafted or rebuilt from now on.
        </p>
      </div>
    </EwPanel>
  );
}
